import React from 'react';
import NavItem from '@minecraft.nav-item';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from '@blocs.i18n';
import { media } from '@minecraft.themes';
import { UpdatesFeed } from '@blocs.braze';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import SCREEN_NAMES from 'config/constants/screenNames';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import { checkIfRouteIsActive } from 'utils/routesUtils';
import { ThemeProps } from 'typed/theme';
import { defaultGlobalPaginations, globalPaginationVars } from 'lib/cache';
import { TALENT_SCOUT_ROUTE } from 'components/TalentScout/TalentScout';
import { useLocation } from 'react-router';

/* ----- Types ----- */
type StyledNavItemProps = {
  isActive: boolean;
  children: JSX.Element;
  onClick: () => void;
} & ThemeProps;

/* ----- Styled Components ----- */
const StyledNavItem = styled(
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  ({ isActive, ...restProps }: StyledNavItemProps): JSX.Element => <NavItem {...restProps} />
)<StyledNavItemProps>`
  color: ${(props: StyledNavItemProps): string => props.theme.colors.primary.darkGrey};
  padding: 0.5rem 0;

  ${media.xsm`
    padding: 0.5rem 0.80rem;
  `}
  ${media.lg`
    color: ${(props: StyledNavItemProps): string => props.theme.colors.primary.white};
  `}
  ${(props: StyledNavItemProps): string => props.isActive && '& a {border-bottom: 1px solid white}'};
`;

const linkNoUnderline: FlattenSimpleInterpolation = css`
  &:hover,
  &:active,
  &:not([href]):not([tabindex]):hover {
    text-decoration: none;
  }
`;

const StyledLink = styled(Link)`
  padding-bottom: 0.12rem;

  &&& {
    text-decoration: none;
    &:hover {
      text-decoration: none;
    }
  }
`;

export const StyledUpdateLink = styled.a`
  ${linkNoUnderline}
`;

/* ----- Main Component ----- */
const Nav = (): JSX.Element => {
  const routerHistory = useHistory();
  const route = useLocation();
  const { t } = useTranslation();

  const handleResetPaginationAndFilter = (): void => {
    globalPaginationVars(defaultGlobalPaginations);
  };

  const navRoutes = {
    projects: ['/projects'],
    packages: ['/packages'],
    roster: ['/roster', '/profile'],
    worksheet: ['/worksheet'],
    reports: ['/reports'],
    talentScout: ['/talent-scout'],
  };
  const routePath: string = route.pathname;
  const canViewTalentScout = useFeatureExperiment(FEATURE_FLAGS.TALENT_SCOUT_PHASE_1);

  return (
    <>
      <StyledNavItem
        isActive={checkIfRouteIsActive(navRoutes.projects, routePath)}
        onClick={handleResetPaginationAndFilter}
        aria-label={t('reps:nav.projectList')}
        className="m-0 ml-4"
      >
        <StyledLink to="/projects?refresh" data-qa-id={`${SCREEN_NAMES.HEADER}-project-link`}>
          {t('reps:nav.projectList')}
        </StyledLink>
      </StyledNavItem>
      <StyledNavItem
        isActive={checkIfRouteIsActive(navRoutes.packages, routePath)}
        onClick={handleResetPaginationAndFilter}
        aria-label={t('reps:nav.packages')}
        className="m-0"
      >
        <StyledLink to="/packages?refresh" data-qa-id={`${SCREEN_NAMES.PACKAGES}-project-link`}>
          {t('reps:nav.packages')}
        </StyledLink>
      </StyledNavItem>
      <StyledNavItem
        isActive={checkIfRouteIsActive(navRoutes.roster, routePath)}
        onClick={handleResetPaginationAndFilter}
        aria-label={t('reps:nav.talent')}
        className="m-0"
      >
        <StyledLink to="/roster?refresh" data-qa-id={`${SCREEN_NAMES.HEADER}-talent-link`}>
          {t('reps:nav.talent')}
        </StyledLink>
      </StyledNavItem>
      <StyledNavItem
        isActive={checkIfRouteIsActive(navRoutes.worksheet, routePath)}
        onClick={handleResetPaginationAndFilter}
        aria-label={t('reps:nav.worksheet')}
        className="m-0"
      >
        <StyledLink to="/worksheet?refresh" data-qa-id={`${SCREEN_NAMES.HEADER}-worksheet-link`}>
          {t('reps:nav.worksheet')}
        </StyledLink>
      </StyledNavItem>
      <StyledNavItem
        isActive={checkIfRouteIsActive(navRoutes.reports, routePath)}
        onClick={handleResetPaginationAndFilter}
        aria-label={t('reps:nav.reports')}
        className="m-0"
      >
        <StyledLink to="/reports" data-qa-id={`${SCREEN_NAMES.HEADER}-reports-link`}>
          {t('reps:nav.reports')}
        </StyledLink>
      </StyledNavItem>
      {canViewTalentScout && (
        <StyledNavItem
          isActive={checkIfRouteIsActive(navRoutes.talentScout, routePath)}
          onClick={handleResetPaginationAndFilter}
          aria-label={t('reps:talentScout.title')}
          className="m-0"
        >
          <StyledLink
            to={TALENT_SCOUT_ROUTE}
            onClick={(): void => routerHistory.push(TALENT_SCOUT_ROUTE)}
            data-qa-id={`${SCREEN_NAMES.HEADER}-talent-scout-link`}
          >
            {t('reps:talentScout.title')}
          </StyledLink>
        </StyledNavItem>
      )}
      <StyledNavItem isActive={false} onClick={null} aria-label={t('common:mainNav.updates')} className="m-0">
        <UpdatesFeed
          wrapperElement={StyledUpdateLink}
          additionalWrapperElProps={{
            'data-qa-id': `${SCREEN_NAMES.HEADER}-braze-feed-updates`,
          }}
        />
      </StyledNavItem>
    </>
  );
};

export default Nav;
